import { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { AlertTriangle, Boxes, ClipboardList, Package, Plus, TrendingUp, Users, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtDate, fmtGNF, STATUS_STYLES } from "@/lib/format";
import { ABtn, Panel } from "./AdminLayout";
import { useNavigate } from "react-router-dom";

const TONES = {
  brand: { icon: "text-emerald-400", ring: "ring-emerald-500/25", glow: "from-emerald-500/15" },
  gold: { icon: "text-amber-400", ring: "ring-amber-500/25", glow: "from-amber-500/15" },
  rose: { icon: "text-rose-400", ring: "ring-rose-500/25", glow: "from-rose-500/15" },
};

const Stat = ({ label, value, icon: Icon, tone = "brand", hint }) => {
  const t = TONES[tone] || TONES.brand;
  return (
    <div
      className="group relative overflow-hidden rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900 to-slate-950 p-4 transition-colors hover:border-slate-700"
      data-testid={`stat-${label}`}
    >
      <div className={`pointer-events-none absolute -right-6 -top-6 h-20 w-20 rounded-full bg-gradient-to-br ${t.glow} to-transparent blur-xl`} />
      <div className="relative flex items-start justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
        <span className={`grid h-8 w-8 place-items-center rounded-lg bg-slate-800/80 ring-1 ${t.ring}`}>
          <Icon className={`h-4 w-4 ${t.icon}`} />
        </span>
      </div>
      <p className="font-num relative mt-3 text-xl font-bold text-white">{value}</p>
      {hint && <p className="relative mt-1 text-[11px] text-slate-500">{hint}</p>}
    </div>
  );
};

export default function Dashboard() {
  const [s, setS] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/admin/stats").then((r) => setS(r.data)).catch(() => toast.error("Erreur de chargement des statistiques"));
  }, []);

  if (!s) return <p className="text-sm text-slate-400" data-testid="dashboard-loading">Chargement du tableau de bord…</p>;

  return (
    <div className="space-y-4" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-white">Tableau de bord</h1>
          <p className="text-xs text-slate-400">Vue d'ensemble de EMPIRE-SHOP</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <ABtn onClick={() => navigate("/admin/produits?nouveau=1")} data-testid="dashboard-add-product">
            <Plus className="h-4 w-4" /> Ajouter un produit
          </ABtn>
          <ABtn variant="ghost" onClick={() => navigate("/admin/vendeurs")} data-testid="dashboard-goto-vendors">
            Demandes vendeurs
          </ABtn>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Chiffre d'affaires" value={fmtGNF(s.revenue)} icon={TrendingUp} hint="Commandes livrées" />
        <Stat label="Marge brute" value={fmtGNF(s.gross_margin)} icon={Wallet} tone="gold" hint={`Coût d'achat ${fmtGNF(s.purchase_cost)}`} />
        <Stat label="Commandes" value={s.orders_total} icon={ClipboardList} hint={`${s.orders_pending} en cours · ${s.orders_delivered} livrées`} />
        <Stat label="Clients" value={s.customers} icon={Users} />
        <Stat label="Produits actifs" value={`${s.products_active}/${s.products_total}`} icon={Package} />
        <Stat label="Valeur du stock" value={fmtGNF(s.stock_value)} icon={Boxes} tone="gold" hint={`Valeur vente ${fmtGNF(s.stock_retail_value)}`} />
        <Stat label="Alertes stock faible" value={s.low_stock_count} icon={AlertTriangle} tone="rose" />
        <Stat label="Taux de livraison" value={`${s.delivery_rate.toFixed(1)} %`} icon={TrendingUp} hint={`Annulations ${s.cancel_rate.toFixed(1)} %`} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Ventes livrées (14 derniers jours)" testid="panel-daily-sales">
          {s.daily_sales.length === 0 ? (
            <p className="text-sm text-slate-500">Aucune vente livrée pour le moment.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={s.daily_sales}>
                <CartesianGrid stroke="#1e293b" vertical={false} />
                <XAxis dataKey="date" stroke="#64748b" fontSize={11} />
                <YAxis stroke="#64748b" fontSize={11} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                <Tooltip
                  contentStyle={{ background: "#0f172a", border: "1px solid #283548", borderRadius: 12, fontSize: 12 }}
                  formatter={(v) => fmtGNF(v)}
                />
                <Line type="monotone" dataKey="montant" stroke="#34d399" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Panel>

        <Panel title="Produits les plus vendus" testid="panel-top-products">
          {s.top_products.length === 0 ? (
            <p className="text-sm text-slate-500">Aucune vente enregistrée.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={s.top_products} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid stroke="#1e293b" horizontal={false} />
                <XAxis type="number" stroke="#64748b" fontSize={11} />
                <YAxis type="category" dataKey="name" stroke="#64748b" fontSize={10} width={120} />
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #283548", borderRadius: 12, fontSize: 12 }} />
                <Bar dataKey="quantite" fill="#D97706" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Stock par catégorie" testid="panel-stock-category">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={s.stock_by_category}>
              <CartesianGrid stroke="#1e293b" vertical={false} />
              <XAxis dataKey="name" stroke="#64748b" fontSize={9} interval={0} angle={-20} textAnchor="end" height={60} />
              <YAxis stroke="#64748b" fontSize={11} />
              <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #283548", borderRadius: 12, fontSize: 12 }} />
              <Bar dataKey="stock" fill="#0D5C3A" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Dernières commandes" testid="panel-recent-orders">
          <div className="space-y-2">
            {s.recent_orders.length === 0 && <p className="text-sm text-slate-500">Aucune commande.</p>}
            {s.recent_orders.map((o) => (
              <div key={o.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-800 p-3">
                <div>
                  <p className="font-num text-sm font-semibold text-white">{o.number}</p>
                  <p className="text-[11px] text-slate-400">{o.customer_name} · {fmtDate(o.created_at)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`ds-chip ${STATUS_STYLES[o.status] || ""}`}>{o.status}</span>
                  <span className="font-num text-sm font-bold text-emerald-400">{fmtGNF(o.total)}</span>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}
